import { useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import type { Task } from "../../mock/types";
import { getInitials, profileColorClass } from "../../mock/helpers";
import { CURRENT_USER } from "../../mock/session";

export function CommentsSection({
	task,
	onAdd,
}: {
	task: Task;
	onAdd: (body: string) => void;
}) {
	const [body, setBody] = useState("");

	const submit = () => {
		const trimmed = body.trim();
		if (!trimmed) return;
		onAdd(trimmed);
		setBody("");
	};

	return (
		<section>
			<div className="mb-3 flex items-center gap-2">
				<MessageSquare className="h-4 w-4 text-muted" />
				<h3 className="text-sm font-semibold text-foreground">
					Comments
				</h3>
				<span className="text-xs text-muted">
					{task.comments.length}
				</span>
			</div>

			<ul className="space-y-3">
				{task.comments.map((c) => (
					<li key={c.id} className="flex gap-2.5">
						<Avatar className="h-7 w-7 shrink-0">
							<AvatarFallback
								className={`text-[10px] text-white ${profileColorClass(c.author.id)}`}
							>
								{getInitials(c.author.full_name ?? c.author.email)}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1">
							<div className="flex items-center gap-2">
								<span className="text-xs font-medium text-foreground">
									{c.author.full_name ?? c.author.email}
								</span>
							</div>
							<p className="text-sm text-foreground/90">{c.body}</p>
						</div>
					</li>
				))}
				{task.comments.length === 0 && (
					<li className="text-xs text-muted">No comments yet.</li>
				)}
			</ul>

			<div className="mt-3 flex items-start gap-2">
				<Avatar className="h-7 w-7 shrink-0">
					<AvatarFallback
						className={`text-[10px] text-white ${profileColorClass(CURRENT_USER.id)}`}
					>
						{getInitials(CURRENT_USER.full_name)}
					</AvatarFallback>
				</Avatar>
				<div className="flex-1">
					<textarea
						value={body}
						onChange={(e) => setBody(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
								e.preventDefault();
								submit();
							}
						}}
						rows={2}
						placeholder="Write a comment..."
						className="w-full resize-none rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
					/>
					<div className="mt-1.5 flex justify-end">
						<Button size="sm" onClick={submit} disabled={!body.trim()}>
							<Send className="h-3.5 w-3.5" />
							Comment
						</Button>
					</div>
				</div>
			</div>
		</section>
	);
}
